/**
 * AIDIA offline PDF text extractor — dependency-free, on-device.
 *
 * Approach: scan the PDF for content streams, decompress any FlateDecode streams with the
 * browser's DecompressionStream, then interpret the text-drawing operators (Tj/TJ/Td/Tm/T*)
 * to reconstruct lines of text. Works on "born-digital" (text-based) statements; scanned/image
 * PDFs produce no text (the caller falls back to the AI path). Nothing leaves the device.
 */
const _mod = (() => {
  const supported = () => typeof DecompressionStream !== "undefined";

  // Latin1 decode (byte value == char code) so string indices map 1:1 to byte offsets.
  function latin1(bytes) {
    let out = "";
    const CHUNK = 0x8000;
    for (let i = 0; i < bytes.length; i += CHUNK) {
      out += String.fromCharCode.apply(null, bytes.subarray(i, i + CHUNK));
    }
    return out;
  }

  async function inflate(bytes, format) {
    try {
      const ds = new DecompressionStream(format);
      const ab = await new Response(new Blob([bytes]).stream().pipeThrough(ds)).arrayBuffer();
      return new Uint8Array(ab);
    } catch {
      return null;
    }
  }
  async function tryInflate(bytes) {
    return (await inflate(bytes, "deflate")) || (await inflate(bytes, "deflate-raw"));
  }

  // Locate raw bytes of every `stream ... endstream` segment.
  function findStreams(bytes) {
    const s = latin1(bytes);
    const res = [];
    let i = 0;
    while (true) {
      const si = s.indexOf("stream", i);
      if (si < 0) break;
      if (s.substr(si - 3, 3) === "end") { i = si + 6; continue; } // skip 'endstream'
      let start = si + 6;
      if (s[start] === "\r") start++;
      if (s[start] === "\n") start++;
      const ei = s.indexOf("endstream", start);
      if (ei < 0) break;
      // The EOL immediately before `endstream` is a delimiter, not stream data —
      // strip it, or DecompressionStream rejects the trailing byte(s).
      let end = ei;
      if (s[end - 1] === "\n") end--;
      if (s[end - 1] === "\r") end--;
      res.push(bytes.subarray(start, end));
      i = ei + 9;
    }
    return res;
  }

  // ---- content-stream token helpers ----
  function readString(str, i) {
    // str[i] === '(' ; returns { text, next }
    let depth = 0, out = "", j = i;
    for (; j < str.length; j++) {
      const c = str[j];
      if (c === "\\") {
        const n = str[j + 1];
        if (n === "n") out += "\n";
        else if (n === "r") out += "\r";
        else if (n === "t") out += "\t";
        else if (n === "b") out += "\b";
        else if (n === "f") out += "\f";
        else if (n >= "0" && n <= "7") {
          let oct = n; let k = j + 2;
          while (k < j + 4 && str[k] >= "0" && str[k] <= "7") { oct += str[k]; k++; }
          out += String.fromCharCode(parseInt(oct, 8));
          j = k - 1; continue;
        } else out += n;
        j++;
      } else if (c === "(") { depth++; out += c; }
      else if (c === ")") { if (depth === 0) { j++; break; } depth--; out += c; }
      else out += c;
    }
    return { text: out, next: j };
  }
  function hexToStr(hex) {
    const h = hex.replace(/\s+/g, "");
    let out = "";
    for (let i = 0; i < h.length; i += 2) out += String.fromCharCode(parseInt(h.substr(i, 2), 16) || 0);
    return out;
  }

  // Turn one content-stream string into text lines using the text operators.
  function extractFromContent(str) {
    const lines = [];
    let cur = "", curY = null;
    const flush = () => { if (cur.trim()) lines.push(cur.trim()); cur = ""; };
    let stack = [];
    const nums = () => stack.filter((t) => t.num != null).map((t) => t.num);
    const lastStr = () => { for (let k = stack.length - 1; k >= 0; k--) if (stack[k].str != null) return stack[k].str; return null; };
    const lastArr = () => { for (let k = stack.length - 1; k >= 0; k--) if (stack[k].arr) return stack[k].arr; return null; };

    let i = 0; const n = str.length;
    while (i < n) {
      const c = str[i];
      if (c === "(") { const r = readString(str, i + 1); stack.push({ str: r.text }); i = r.next; continue; }
      if (c === "[") {
        // TJ array
        const arr = []; let j = i + 1;
        while (j < n && str[j] !== "]") {
          if (str[j] === "(") { const r = readString(str, j + 1); arr.push({ str: r.text }); j = r.next; }
          else if (str[j] === "<") { const e = str.indexOf(">", j); arr.push({ str: hexToStr(str.slice(j + 1, e)) }); j = e + 1; }
          else if (/[-\d.]/.test(str[j])) { let k = j; while (k < n && /[-\d.]/.test(str[k])) k++; arr.push({ num: parseFloat(str.slice(j, k)) }); j = k; }
          else j++;
        }
        stack.push({ arr }); i = j + 1; continue;
      }
      if (c === "<") {
        if (str[i + 1] === "<") { i += 2; continue; } // dict — ignore
        const e = str.indexOf(">", i); stack.push({ str: hexToStr(str.slice(i + 1, e)) }); i = e + 1; continue;
      }
      if (c === "/") { i++; while (i < n && !/[\s\[\]\/<>()]/.test(str[i])) i++; continue; }
      if (/[-\d.]/.test(c)) { let j = i; while (j < n && /[-\d.]/.test(str[j])) j++; stack.push({ num: parseFloat(str.slice(i, j)) }); i = j; continue; }
      if (/[A-Za-z'"*]/.test(c)) {
        let j = i; while (j < n && /[A-Za-z0-9*'"]/.test(str[j])) j++;
        handleOp(str.slice(i, j)); i = j; continue;
      }
      i++;
    }
    flush();

    function handleOp(op) {
      if (op === "Tj") { const s = lastStr(); if (s != null) cur += s; }
      else if (op === "'" || op === '"') { flush(); const s = lastStr(); if (s != null) cur += s; }
      else if (op === "TJ") {
        const a = lastArr();
        if (a) for (const el of a) {
          if (el.str != null) cur += el.str;
          else if (el.num != null && el.num < -120) cur += " ";
        }
      } else if (op === "Td" || op === "TD") {
        const ns = nums(); const ty = ns.length ? ns[ns.length - 1] : 0;
        const newY = (curY || 0) + ty;
        if (Math.abs(ty) > 2) flush(); else if (cur) cur += " ";
        curY = newY;
      } else if (op === "Tm") {
        const ns = nums(); const f = ns.length ? ns[ns.length - 1] : null;
        if (curY != null && f != null && Math.abs(f - curY) > 2) flush();
        else if (cur) cur += " ";
        curY = f;
      } else if (op === "T*") { flush(); }
      else if (op === "BT" || op === "ET") { flush(); curY = null; }
      stack = [];
    }
    return lines.join("\n");
  }

  /** Extract all reconstructed text from a PDF ArrayBuffer. */
  async function extractText(buffer) {
    if (!supported()) throw new Error("This browser can't decompress PDFs here.");
    const bytes = new Uint8Array(buffer);
    const out = [];
    for (const raw of findStreams(bytes)) {
      let data = await tryInflate(raw);
      if (!data) data = raw; // maybe an uncompressed content stream
      const str = latin1(data);
      if (str.includes("Tj") || str.includes("TJ")) out.push(extractFromContent(str));
    }
    return out.join("\n");
  }

  return { extractText, supported };
})();

// --- ES module surface -------------------------------------------------
export const { extractText, supported } = _mod;
export default _mod;
